import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import analyticsService from '../services/analyticsService';

const PriceChart = ({ token = 'TON' }) => {
  const [priceData, setPriceData] = useState([]);
  const [period, setPeriod] = useState('24h');
  const [loading, setLoading] = useState(false);
  const [priceChange, setPriceChange] = useState(0);

  const fetchPriceHistory = async (newPeriod) => {
    setLoading(true);
    try {
      const data = await analyticsService.getPriceHistory(token, newPeriod);
      const history = data.data || [];
      
      // Format for chart
      const formatted = history.map((item) => ({
        time: new Date(item.timestamp).toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit' 
        }),
        price: parseFloat(item.price),
        volume: item.volume_24h,
      }));
      
      setPriceData(formatted);
      
      // Calculate price change
      if (formatted.length >= 2) {
        const first = formatted[0].price;
        const last = formatted[formatted.length - 1].price;
        const change = ((last - first) / first) * 100;
        setPriceChange(change);
      }
    } catch (error) {
      toast.error('Failed to load price data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPriceHistory(period);
    const interval = setInterval(() => fetchPriceHistory(period), 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [period, token]);

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    fetchPriceHistory(newPeriod);
  };

  const currentPrice = priceData.length > 0 ? priceData[priceData.length - 1].price : 0;

  return (
    <Card data-testid="price-chart-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              {token} Price
              {priceChange !== 0 && (
                <span className={`text-sm font-normal flex items-center gap-1 ${
                  priceChange > 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {priceChange > 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                  {Math.abs(priceChange).toFixed(2)}%
                </span>
              )}
            </CardTitle>
            <CardDescription>
              <span className="text-2xl font-bold text-gray-900">
                ${currentPrice.toFixed(2)}
              </span>
            </CardDescription>
          </div>
          
          <Tabs value={period} onValueChange={handlePeriodChange} className="w-auto">
            <TabsList>
              <TabsTrigger value="1h">1H</TabsTrigger>
              <TabsTrigger value="24h">24H</TabsTrigger>
              <TabsTrigger value="7d">7D</TabsTrigger>
              <TabsTrigger value="30d">30D</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      <CardContent>
        {loading && priceData.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-gray-500">Loading chart...</p>
          </div>
        ) : priceData.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-gray-500">No price data available</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={priceData}>
              <defs>
                <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="time" 
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
                interval="preserveStartEnd"
              />
              <YAxis 
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
                domain={['auto', 'auto']}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                }}
                formatter={(value) => [`$${parseFloat(value).toFixed(4)}`, 'Price']}
              />
              <Area 
                type="monotone" 
                dataKey="price" 
                stroke="#3b82f6" 
                strokeWidth={2}
                fill="url(#colorPrice)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default PriceChart;
